/**
 * Data-source config envelope primitives shared with Web through the existing
 * API source contract boundary.
 *
 * This module intentionally has no framework, validation-library, or Node.js
 * imports because Web consumes API source files for shared runtime types.
 * API-only validation and packing live in `data-source-config-extensions.util.ts`.
 */
export const DATA_SOURCE_CONFIG_EXTENSIONS_KEY = '__dataSourceConfigExtensions';

interface ExtensionEntry {
	path: Array<string | number>;
	values: Record<string, unknown>;
}

export interface DataSourceConfigObjectSchema {
	kind: 'object';
	fields: Record<string, DataSourceConfigSchema>;
}

interface DataSourceConfigArraySchema {
	kind: 'array';
	item: DataSourceConfigSchema;
}

interface DataSourceConfigOneOrManySchema {
	kind: 'one-or-many';
	item: DataSourceConfigSchema;
}

interface DataSourceConfigOpenSchema {
	kind: 'open';
	credentialKeysAllowed?: boolean;
}

export type DataSourceConfigSchema =
	| DataSourceConfigObjectSchema
	| DataSourceConfigArraySchema
	| DataSourceConfigOneOrManySchema
	| DataSourceConfigOpenSchema
	| { kind: 'leaf' };

const leaf: DataSourceConfigSchema = { kind: 'leaf' };
const extensionMap: DataSourceConfigSchema = { kind: 'open' };
const valueMap: DataSourceConfigSchema = { kind: 'open', credentialKeysAllowed: true };
const array = (item: DataSourceConfigSchema): DataSourceConfigSchema => ({ kind: 'array', item });
const oneOrMany = (item: DataSourceConfigSchema): DataSourceConfigSchema => ({ kind: 'one-or-many', item });
const object = (fields: Record<string, DataSourceConfigSchema>): DataSourceConfigObjectSchema => ({ kind: 'object', fields });

const mappingItem = object({ path: leaf, displayName: leaf, format: leaf, localizable: leaf });
const sortItem = object({ path: leaf, displayName: leaf });
const filterExtra = object({ maskPath: leaf });
const filterOption = object({ id: leaf, name: leaf, value: valueMap, entityName: leaf, extra: filterExtra });
const filterItem = object({
	id: leaf,
	name: leaf,
	category: leaf,
	paramKey: leaf,
	type: leaf,
	layout: leaf,
	grow: leaf,
	topPadding: leaf,
	prefixIcon: leaf,
	options: array(filterOption),
	globalSettingsOptionsKey: leaf,
	value: oneOrMany(filterOption),
	entityName: leaf,
	matchEndpoint: leaf,
	order: leaf,
	extra: filterExtra
});
const rightsEntry = object({ path: leaf });
const rightsStatusEntry = object({ path: leaf, valueMap, deriveFrom: leaf, startPath: leaf, endPath: leaf });
const rightsFieldMap = object({
	status: rightsStatusEntry,
	expiresAt: rightsEntry,
	restrictions: rightsEntry,
	licenseType: rightsEntry,
	raw: rightsEntry
});
const queryParams = object({
	search: leaf,
	filter: extensionMap,
	mediaType: leaf,
	doNotMap: leaf,
	sort: leaf,
	sortStrategy: leaf,
	pageSize: leaf,
	page: leaf,
	cursor: leaf,
	ids: leaf
});

export const DATA_SOURCE_CONFIG_SCHEMA = object({
	target: leaf,
	type: leaf,
	context: leaf,
	mapping: array(mappingItem),
	params: queryParams,
	sortableFields: array(sortItem),
	filters: array(filterItem),
	assetPathProperty: leaf,
	assetTitleProperty: leaf,
	previewPath: leaf,
	accessToken: leaf,
	tagPath: leaf,
	attribution: leaf,
	saveToLibrary: leaf,
	rightsFieldMap,
	enterpriseId: leaf,
	clientId: leaf,
	baseUrl: leaf,
	apiKey: leaf,
	apiSecret: leaf,
	clientSecret: leaf,
	developerToken: leaf,
	refreshToken: leaf,
	jwtPrivateKey: leaf,
	jwtPrivateKeyPassphrase: leaf,
	jwtKeyId: leaf,
	columnSeparator: leaf,
	environment: leaf,
	timeout: leaf,
	pageSize: leaf,
	linkExpireInDays: leaf,
	includeSubfolders: leaf,
	includeSharedLinks: leaf,
	loadFullFolderTree: leaf,
	useBriaIframe: leaf,
	authMethod: leaf,
	allowedFileTypes: leaf,
	supportedInputs: leaf,
	aspectRatios: leaf,
	models: extensionMap
});

function schemaFieldNames(schema: DataSourceConfigSchema): readonly string[] {
	return schema.kind === 'object' ? Object.freeze(Object.keys(schema.fields)) : [];
}

export const DATA_SOURCE_CONFIG_EXTENSION_SCHEMA_FIELDS = Object.freeze({
	config: schemaFieldNames(DATA_SOURCE_CONFIG_SCHEMA),
	mappingItem: schemaFieldNames(mappingItem),
	sortItem: schemaFieldNames(sortItem),
	filterExtra: schemaFieldNames(filterExtra),
	filterOption: schemaFieldNames(filterOption),
	filterItem: schemaFieldNames(filterItem),
	rightsEntry: schemaFieldNames(rightsEntry),
	rightsStatusEntry: schemaFieldNames(rightsStatusEntry),
	rightsFieldMap: schemaFieldNames(rightsFieldMap),
	queryParams: schemaFieldNames(queryParams)
});

const ROOT_CREDENTIAL_KEYS = new Set([
	'accessToken',
	'apiKey',
	'apiSecret',
	'clientSecret',
	'developerToken',
	'refreshToken',
	'jwtPrivateKey',
	'jwtPrivateKeyPassphrase'
]);
const PROTOTYPE_KEYS = new Set(['__proto__', 'prototype', 'constructor']);

export class DataSourceConfigEnvelopeError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'DataSourceConfigEnvelopeError';
	}
}

function invalidMetadata(): never {
	throw new DataSourceConfigEnvelopeError('Data source connector configuration contains invalid extension metadata.');
}

/** Names the offending field so the caller can act on the error. Never echoes the
 * value: what these checks reject is, by definition, a credential. */
function describeField(path: ReadonlyArray<string | number>): string {
	return path.length > 0 ? ` at "${path.join('.')}"` : '';
}

function unsupportedField(path: ReadonlyArray<string | number> = []): never {
	throw new DataSourceConfigEnvelopeError(
		`Data source connector configuration contains an unsupported extension field${describeField(path)}.`
	);
}

function unsupportedValue(path: ReadonlyArray<string | number> = []): never {
	throw new DataSourceConfigEnvelopeError(
		`Data source connector configuration contains an unsupported extension value${describeField(path)}.`
	);
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function isCredentialLikeDataSourceConfigKey(key: string): boolean {
	const normalized = key.replace(/[^a-z0-9]/gi, '').toLowerCase();
	const words = key
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.toLowerCase()
		.split(/[^a-z0-9]+/)
		.filter(Boolean);
	return (
		['password', 'passphrase', 'secret', 'token', 'credential', 'authorization', 'cookie', 'apikey', 'privatekey'].some(fragment =>
			normalized.includes(fragment)
		) ||
		words.some(word => ['access', 'auth', 'bearer'].includes(word)) ||
		words.includes('key')
	);
}

function assertSafeKey(key: string, path: ReadonlyArray<string | number> = []): void {
	if (PROTOTYPE_KEYS.has(key) || key === DATA_SOURCE_CONFIG_EXTENSIONS_KEY || isCredentialLikeDataSourceConfigKey(key)) {
		unsupportedField([...path, key]);
	}
}

export function hasSensitiveDataSourceUrl(value: unknown): boolean {
	if (typeof value !== 'string') {
		return false;
	}
	try {
		const url = new URL(value.trim());
		if (url.username || url.password) {
			return true;
		}
		for (const key of url.searchParams.keys()) {
			const normalized = key.replace(/[^a-z0-9]/gi, '').toLowerCase();
			if (
				isCredentialLikeDataSourceConfigKey(key) ||
				normalized === 'sig' ||
				normalized === 'sas' ||
				normalized.includes('signature')
			) {
				return true;
			}
		}
		return false;
	} catch {
		return /^[a-z][a-z\d+.-]*:\/\/[^/?#]*@/i.test(value.trim());
	}
}

function cloneAndAssertSafe(value: unknown, path: ReadonlyArray<string | number> = []): unknown {
	if (typeof value === 'string' && hasSensitiveDataSourceUrl(value)) {
		unsupportedValue(path);
	}
	if (value === null || ['string', 'number', 'boolean'].includes(typeof value)) {
		return value;
	}
	if (Array.isArray(value)) {
		return value.map((item, index) => cloneAndAssertSafe(item, [...path, index]));
	}
	if (!isRecord(value)) {
		unsupportedValue(path);
	}

	const result: Record<string, unknown> = {};
	for (const [key, child] of Object.entries(value)) {
		assertSafeKey(key, path);
		result[key] = cloneAndAssertSafe(child, [...path, key]);
	}
	return result;
}

function cloneConfigValue(value: unknown): unknown {
	if (Array.isArray(value)) {
		return value.map(cloneConfigValue);
	}
	if (!isRecord(value)) {
		return value;
	}

	const result = Object.create(Object.getPrototypeOf(value)) as Record<string, unknown>;
	for (const [key, child] of Object.entries(value)) {
		if (key !== DATA_SOURCE_CONFIG_EXTENSIONS_KEY) {
			Object.defineProperty(result, key, {
				value: cloneConfigValue(child),
				enumerable: true,
				writable: true,
				configurable: true
			});
		}
	}
	return result;
}

export function unpackDataSourceConfigExtensions<T>(config: T): T {
	if (!isRecord(config)) {
		return config;
	}

	const result = cloneConfigValue(config) as Record<string, unknown>;
	const entries = config[DATA_SOURCE_CONFIG_EXTENSIONS_KEY];
	if (entries === undefined) {
		return result as T;
	}
	if (!Array.isArray(entries) || entries.length > 256) {
		invalidMetadata();
	}

	for (const entry of entries as ExtensionEntry[]) {
		if (!isRecord(entry) || !Array.isArray(entry.path) || !isRecord(entry.values)) {
			invalidMetadata();
		}
		let destination: unknown = result;
		for (const segment of entry.path) {
			if (
				(typeof segment !== 'string' && typeof segment !== 'number') ||
				(typeof segment === 'string' && (PROTOTYPE_KEYS.has(segment) || segment === DATA_SOURCE_CONFIG_EXTENSIONS_KEY)) ||
				(typeof segment === 'number' && (!Number.isInteger(segment) || segment < 0)) ||
				destination === null ||
				typeof destination !== 'object'
			) {
				invalidMetadata();
			}
			destination = (destination as Record<string | number, unknown>)[segment];
		}
		if (!isRecord(destination)) {
			invalidMetadata();
		}
		for (const [key, value] of Object.entries(entry.values)) {
			assertSafeKey(key, entry.path);
			if (!(key in destination)) {
				destination[key] = cloneAndAssertSafe(value, [...entry.path, key]);
			}
		}
	}

	return result as T;
}

/** Builds a secret-free clone for public/export responses, including safe
 * provider extensions while dropping stored credentials and unsafe legacy keys. */
export function sanitizeDataSourceConfigForPublic<T>(config: T): T {
	const visit = (value: unknown, schema: DataSourceConfigSchema | undefined, root: boolean): unknown => {
		const effectiveSchema = schema?.kind === 'one-or-many' ? schema.item : schema;
		if (Array.isArray(value)) {
			const itemSchema =
				effectiveSchema?.kind === 'array' ? effectiveSchema.item : schema?.kind === 'one-or-many' ? schema.item : undefined;
			return value.filter(item => !hasSensitiveDataSourceUrl(item)).map(item => visit(item, itemSchema, false));
		}
		if (!isRecord(value)) {
			return value;
		}

		const objectSchema = effectiveSchema?.kind === 'object' ? effectiveSchema : undefined;
		const openCredentialKeysAllowed = effectiveSchema?.kind === 'open' && effectiveSchema.credentialKeysAllowed === true;
		const result: Record<string, unknown> = {};
		for (const [key, child] of Object.entries(value)) {
			const childSchema = objectSchema?.fields[key];
			if (
				key === DATA_SOURCE_CONFIG_EXTENSIONS_KEY ||
				PROTOTYPE_KEYS.has(key) ||
				(root && ROOT_CREDENTIAL_KEYS.has(key)) ||
				(!childSchema && !openCredentialKeysAllowed && isCredentialLikeDataSourceConfigKey(key)) ||
				hasSensitiveDataSourceUrl(child)
			) {
				continue;
			}
			result[key] = visit(child, childSchema ?? (effectiveSchema?.kind === 'open' ? effectiveSchema : undefined), false);
		}
		return result;
	};

	return visit(unpackDataSourceConfigExtensions(config), DATA_SOURCE_CONFIG_SCHEMA, true) as T;
}
